import { useMemo, useState } from "react";
import dayjs from "dayjs";
import clsx from "clsx";
import { db } from "../../lib/api";
import { useQuery } from "../../lib/hooks";
import { Link, setQuery, useRoute } from "../../lib/router";
import { useSession } from "../../lib/session";
import { PageHead } from "../../components/Shell";
import { Empty, ErrorBox, Loading, Panel, fmtShort, money } from "../../components/ui";
import { useLog } from "../../components/QuickLog";
import { REVIEW_STEPS, stageIndex } from "../../components/ReviewSteps";

type Row = { person_id: string; full_name: string; phone: string | null; email: string | null; next_annual_review: string | null; review_status: string | null;
  last_annual_review: string | null; done_this_year: boolean; done_on: string | null; review_id: string | null; review_type: string | null; stage: string;
  status: string | null; scheduled_at: string | null; prepared_on: string | null; invited_on: string | null; chased_on: string | null; chase_count: number;
  followup_sent_on: string | null; last_step_on: string | null; book_value: number };
type Bucket = "all" | "done" | "booked" | "progress" | "not_started" | "overdue" | "followup";

const bucketOf = (r: Row): Bucket => r.done_this_year ? "done" : r.stage === "Booked" || r.stage === "Rescheduled" ? "booked"
  : r.stage === "Not started" || r.stage === "Declined" ? "not_started" : "progress";

/** Clients → Annual reviews: who's done this year, who's booked, who's in progress, who hasn't started. */
export default function Reviews() {
  const { query } = useRoute();
  const { can } = useSession();
  const log = useLog();
  const year = Number(query.get("y") ?? dayjs().year());
  const f = (query.get("f") ?? "all") as Bucket;
  const [sort, setSort] = useState<"due" | "value" | "name">("due");
  const q = useQuery(`reviews:${year}`, () => db.rpc<Row[]>("review_tracker", { p_year: year }), { staleMs: 30_000 });
  // couples doing one review together
  const joint = useQuery("reviews:joint", () => db.select<{ person_id: string; joint_with: string; people: { full_name: string } | null }>("client_reviews",
    { joint_with: "not.is.null", status: "in.(To book,Booked,Rescheduled,Missed,Completed)", select: "person_id,joint_with,people!client_reviews_joint_with_fkey(full_name)" }), { staleMs: 30_000 });
  const jointOf = (pid: string) => (joint.data ?? []).find((j) => j.person_id === pid)?.people?.full_name;
  const rows = q.data ?? [];
  const n = (b: Bucket) => b === "all" ? rows.length : b === "overdue" ? rows.filter((r) => !r.done_this_year && r.review_status === "Overdue").length
    : b === "followup" ? rows.filter((r) => r.done_this_year && !r.followup_sent_on).length : rows.filter((r) => bucketOf(r) === b).length;
  const shown = useMemo(() => rows.filter((r) => f === "all" || (f === "overdue" ? !r.done_this_year && r.review_status === "Overdue"
      : f === "followup" ? r.done_this_year && !r.followup_sent_on : bucketOf(r) === f))
    .sort((a, b) => sort === "value" ? b.book_value - a.book_value : sort === "name" ? a.full_name.localeCompare(b.full_name)
      : (a.next_annual_review ?? "9999").localeCompare(b.next_annual_review ?? "9999")), [rows, f, sort]);
  const pct = rows.length ? Math.round((n("done") / rows.length) * 100) : 0;
  const tile = (b: Bucket, label: string, sub: string, cls = "") => (
    <button className={clsx("tile clickable", cls, f === b && "on")} onClick={() => setQuery({ f: b === "all" ? null : b })}>
      <span className="tv">{n(b)}</span><span className="tl">{label}</span><span className="ts">{sub}</span>
    </button>
  );
  const next = (r: Row) => {
    if (r.done_this_year) return r.followup_sent_on ? null : REVIEW_STEPS[4];
    const i = stageIndex(r.stage);
    return REVIEW_STEPS[Math.min(i + 1, 3)];
  };

  return (
    <>
      <PageHead title="Annual reviews" sub={`Every client's annual review for ${year}: done, booked, in progress or not started. Log each step from here or from the client.`}
        actions={<div className="seg">{[year - 1, year, year + 1].map((y) => <button key={y} aria-pressed={y === year} onClick={() => setQuery({ y: y === dayjs().year() ? null : String(y) })}>{y}</button>)}</div>} />
      <ErrorBox error={q.error} />
      {q.loading && !q.data ? <Loading rows={6} /> : <>
        <div className="tiles auto">
          {tile("all", "Clients", `${pct}% reviewed in ${year}`)}
          {tile("done", "Done", `annual reviews held in ${year}`, "t-green")}
          {tile("booked", "Booked", "meeting on the calendar")}
          {tile("progress", "In progress", "documents ready or times proposed")}
          {tile("not_started", "Not started", "nothing logged yet", n("not_started") ? "t-amber" : "")}
          {tile("overdue", "Overdue", "past their review date", n("overdue") ? "t-red alert" : "t-green")}
          {tile("followup", "Follow-up to send", "held, but no summary sent")}
        </div>
        <div className="progress" style={{ height: 8, margin: "4px 0 16px" }} aria-label={`${pct}% done`}><i style={{ width: `${pct}%`, background: "var(--warm)" }} /></div>
        <Panel actions={<div className="seg">{([["due", "By due date"], ["value", "Biggest first"], ["name", "A–Z"]] as const).map(([k, l]) =>
          <button key={k} aria-pressed={sort === k} onClick={() => setSort(k)}>{l}</button>)}</div>}
          title={f === "all" ? "All clients" : { done: "Done", booked: "Booked", progress: "In progress", not_started: "Not started", overdue: "Overdue", followup: "Follow-up to send" }[f]}>
          {!shown.length ? <Empty title="No one here">Nice.</Empty> : (
            <div className="table-wrap"><table className="data">
              <thead><tr><th>Client</th><th>Review due</th><th>Where it stands</th><th>Steps</th><th className="num">Book value</th><th /></tr></thead>
              <tbody>{shown.map((r) => {
                const nx = next(r);
                const at = r.done_this_year ? 3 + (r.followup_sent_on ? 1 : 0) : stageIndex(r.stage);
                return (
                  <tr key={r.person_id}>
                    <td><Link to={`/clients/${r.person_id}`} className="linkish"><b>{r.full_name}</b></Link>{jointOf(r.person_id) && <span className="pill" style={{ marginLeft: 6 }}>joint with {jointOf(r.person_id)}</span>}<div className="small muted">{r.phone ?? r.email ?? ""}</div></td>
                    <td className="nowrap">{r.next_annual_review ? fmtShort(r.next_annual_review) : "—"}
                      {r.review_status === "Overdue" && !r.done_this_year && <div><span className="pill alert">Overdue</span></div>}</td>
                    <td>{r.done_this_year ? <><span className="pill warm stage-pill">Done {fmtShort(r.done_on)}</span>{r.followup_sent_on ? <div className="small muted">Follow-up sent</div> : null}</>
                      : <><span className={clsx("pill stage-pill", r.stage === "Booked" && "team", r.stage === "Not started" && "cooling")}>{r.stage}</span>
                        <div className="small muted">{r.stage === "Booked" && r.scheduled_at ? dayjs(r.scheduled_at).format("ddd MMM D, h:mm A")
                          : r.last_step_on ? `last step ${fmtShort(r.last_step_on)}` : ""}{r.chase_count ? ` · reminded ${r.chase_count}×` : ""}</div></>}</td>
                    <td><div className="mini-steps" aria-label={`${at + 1} of ${REVIEW_STEPS.length} steps`}>{REVIEW_STEPS.map((s, i) => <i key={s.stage} title={s.label} className={clsx(i <= at && "on")} />)}</div></td>
                    <td className="num">{r.book_value ? money(r.book_value) : "—"}</td>
                    <td className="num">{can("activities", "edit") && nx && <button className="btn small" onClick={() => log({ area: "clients", person_id: r.person_id, person_name: r.full_name, activity_type: nx.activity })}>Log: {nx.label}</button>}</td>
                  </tr>
                );
              })}</tbody>
            </table></div>
          )}
        </Panel>
      </>}
    </>
  );
}
